/**
 * A catalog entry id — `source[:sourceType][:sourceDocumentType]`, as the API
 * builds it. The hierarchy refers to entries by this.
 */
export type KpiSourceId = string;

/** Chips the admin list shows on an entry, as the API tags them. */
export type KpiSourceTag =
  'Foresight Collection' | 'Document Hub' | 'Third party' | 'Manual entry' | 'Managed services' | 'AI';

/**
 * One combination of the three provenance columns a KPI source row carries,
 * as the API ships it in `/configuration.kpiSources`. The catalog is listed in
 * the API's default priority order (index 0 wins), with pinned entries last.
 */
export interface KpiSourceCatalogEntry {
  id: KpiSourceId;
  /** The channel — the API's `KpiCustomDataSource` value. */
  source: string;
  /** Who produced the value: `manual`, `import`, `ai_extraction`, or '' when there is no producer. */
  sourceType: string;
  /** The document kind, or null when the combination is not document-specific. */
  sourceDocumentType: string | null;
  label: string;
  description?: string;
  tags: KpiSourceTag[];
  /** Always ranks last and cannot be re-ordered or excluded. */
  pinned?: boolean;
}

export function createKpiSourceCatalogEntry(
  overrides: Partial<KpiSourceCatalogEntry> = {}
): KpiSourceCatalogEntry {
  const source = overrides.source ?? '';
  const sourceType = overrides.sourceType ?? '';
  const sourceDocumentType = overrides.sourceDocumentType ?? null;
  return {
    id: [source, sourceType, sourceDocumentType].filter(Boolean).join(':'),
    source,
    sourceType,
    sourceDocumentType,
    label: '',
    tags: [],
    ...overrides,
  };
}

/**
 * Keep only entries that carry the catalog contract. An API still serving an
 * older `kpiSources` shape (no `id`, no `tags`) then yields an empty catalog,
 * which the page renders as "not available" rather than failing to render.
 */
export function sanitizeKpiSourceCatalog(raw: unknown): KpiSourceCatalogEntry[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.flatMap((candidate): KpiSourceCatalogEntry[] => {
    if (typeof candidate !== 'object' || candidate === null) {
      return [];
    }
    const entry = candidate as Record<string, unknown>;
    if (
      typeof entry.id !== 'string' ||
      typeof entry.source !== 'string' ||
      typeof entry.label !== 'string' ||
      !Array.isArray(entry.tags)
    ) {
      return [];
    }
    // A well-formed entry round-trips unchanged; optional fields are only set when present.
    const sanitized: KpiSourceCatalogEntry = {
      id: entry.id,
      source: entry.source,
      sourceType: typeof entry.sourceType === 'string' ? entry.sourceType : '',
      sourceDocumentType: typeof entry.sourceDocumentType === 'string' ? entry.sourceDocumentType : null,
      label: entry.label,
      tags: entry.tags.filter((tag): tag is KpiSourceTag => typeof tag === 'string'),
    };
    if (typeof entry.description === 'string') {
      sanitized.description = entry.description;
    }
    if (entry.pinned === true) {
      sanitized.pinned = true;
    }
    return [sanitized];
  });
}

/**
 * How a KPI cell picks between its sources — the API's `KpiSourceResolutionStrategy`.
 * `latest` is the API's default for every tenant: the most recently received value
 * wins and the order below is ignored; `hierarchy` ranks by that order.
 */
export const KPI_SOURCE_RESOLUTION_STRATEGIES = ['latest', 'hierarchy'] as const;
export type KpiSourceResolutionStrategy = (typeof KPI_SOURCE_RESOLUTION_STRATEGIES)[number];

export function isKpiSourceResolutionStrategy(value: unknown): value is KpiSourceResolutionStrategy {
  return KPI_SOURCE_RESOLUTION_STRATEGIES.includes(value as KpiSourceResolutionStrategy);
}

export const DEFAULT_KPI_SOURCE_RESOLUTION_STRATEGY: KpiSourceResolutionStrategy = 'latest';

/** A strategy as the API ships it in `/configuration.kpiResolutionStrategies`, default first. */
export interface KpiResolutionStrategyEntry {
  id: KpiSourceResolutionStrategy;
  label: string;
  description: string;
  /** Whether the order of the hierarchy means anything; sources can be excluded under every strategy. */
  orderable: boolean;
}

/**
 * Keep only strategies that carry the contract. An API that predates strategies
 * ships no list, and the page then offers no choice — it behaves as it did.
 */
export function sanitizeKpiResolutionStrategies(raw: unknown): KpiResolutionStrategyEntry[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.flatMap((candidate): KpiResolutionStrategyEntry[] => {
    if (typeof candidate !== 'object' || candidate === null) {
      return [];
    }
    const entry = candidate as Record<string, unknown>;
    if (
      !isKpiSourceResolutionStrategy(entry.id) ||
      typeof entry.label !== 'string' ||
      typeof entry.description !== 'string' ||
      typeof entry.orderable !== 'boolean'
    ) {
      return [];
    }
    return [
      {
        id: entry.id,
        label: entry.label,
        description: entry.description,
        orderable: entry.orderable,
      },
    ];
  });
}

export interface KpiSourceHierarchyEntry {
  id: KpiSourceId;
  /** Excluded entries stay in the list so re-enabling one restores its slot. */
  enabled: boolean;
}

export interface KpiSourceHierarchyPreferences {
  strategy: KpiSourceResolutionStrategy;
  /** Priority order under `hierarchy`; under `latest` only `enabled` matters, but the order is kept. */
  sources: KpiSourceHierarchyEntry[];
}

/**
 * Values people type into Foresight. Held at the top of the hierarchy and not
 * movable or excludable until product decides how a manual edit should behave
 * when it ranks below a synced source — today the API would store the edit but
 * keep showing the higher-ranked value, with nothing telling the person why.
 */
export const USER_ENTERED_KPI_SOURCE = 'user';

export function isLockedKpiSource(entry: Pick<KpiSourceCatalogEntry, 'source'>): boolean {
  return entry.source === USER_ENTERED_KPI_SOURCE;
}

export function configurableKpiSources(catalog: KpiSourceCatalogEntry[]): KpiSourceCatalogEntry[] {
  return catalog.filter((entry) => !entry.pinned);
}

export function pinnedKpiSources(catalog: KpiSourceCatalogEntry[]): KpiSourceCatalogEntry[] {
  return catalog.filter((entry) => entry.pinned);
}

export function getDefaultKpiSourceHierarchy(
  catalog: KpiSourceCatalogEntry[]
): KpiSourceHierarchyPreferences {
  return normalizeKpiSourceHierarchy(null, catalog);
}

/**
 * Coerce a stored preference into a complete hierarchy over the catalog:
 * unknown and pinned ids are dropped, duplicates keep their first slot, any
 * configurable entry the stored value predates is appended enabled — so a
 * combination added to the catalog later surfaces without a data fix — and
 * the locked entries are held first and enabled whatever the stored value says.
 * A stored value with no (or an unknown) strategy is `latest`: that is how the
 * API resolves it, and the page must show what the API does.
 */
export function normalizeKpiSourceHierarchy(
  stored: Partial<KpiSourceHierarchyPreferences> | null | undefined,
  catalog: KpiSourceCatalogEntry[]
): KpiSourceHierarchyPreferences {
  const strategy = isKpiSourceResolutionStrategy(stored?.strategy)
    ? stored.strategy
    : DEFAULT_KPI_SOURCE_RESOLUTION_STRATEGY;
  const configurable = configurableKpiSources(catalog);
  const byId = new Map(configurable.map((entry) => [entry.id, entry]));
  const seen = new Set<KpiSourceId>();
  const sources: KpiSourceHierarchyEntry[] = [];

  for (const entry of stored?.sources ?? []) {
    if (!byId.has(entry.id) || seen.has(entry.id)) {
      continue;
    }
    seen.add(entry.id);
    sources.push({ id: entry.id, enabled: entry.enabled !== false });
  }

  for (const entry of configurable) {
    if (!seen.has(entry.id)) {
      sources.push({ id: entry.id, enabled: true });
    }
  }

  const isLocked = (entry: KpiSourceHierarchyEntry) => isLockedKpiSource(byId.get(entry.id)!);
  const locked = sources.filter(isLocked).map((entry) => ({ ...entry, enabled: true }));
  return { strategy, sources: [...locked, ...sources.filter((entry) => !isLocked(entry))] };
}
